import { Card, notification, RadioChangeEvent } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, useHistory, useLocation } from 'react-router-dom';
import { SelectedAccounts } from '../../../../Models/Request/Account/AccountLink';
import LinkedDelinkedAccountResponse, {
  LinkedDelinkedAccountObject,
} from '../../../../Models/Response/Account/LinkedDelinkedAccounts';
import { getLinkedAccountsOfUser } from '../../../../Services/AccountsServices/AccountService';
import {
  consentApprovalInitiation,
  consentApprove,
  updateConsentStatus,
  updateConsentStatusVerification,
} from '../../../../Services/ConsentServices/ConsentService';
import { encode } from '../../../../Services/RedirectionServices/Redirection';
import { authType } from '../../../../Utils/Constants/authType';
import {
  ConsentApprovalStatusType,
  ConsentStatusType,
} from '../../../../Utils/Constants/consentTypes';
import { Routes } from '../../../../Utils/Constants/routes';
import getIsRedirection from '../../../../Utils/redirectionUtility';
import ConsentStatusApproval from '../../../Consent/Components/StatusApproval';
import ActiveConsent from '../CosentsByType/ActiveConsent';
import PausedConsent from '../CosentsByType/PausedConsent';
import PendingConsent from '../CosentsByType/PendingConsent';
import RejectedExpiredConsent from '../CosentsByType/RejectedExpiredConsent';
import RevokedConsent from '../CosentsByType/RevokedConsent';
import './index.css';

interface IConsentDetailsProps {
  consentUniqueId: string;
  type: string;
}

const ConsentDetails: React.FC<IConsentDetailsProps> = ({
  type,
  consentUniqueId,
}) => {
  const [selectedCard, setSelectedCard] = useState(new Array<string>());
  const [isLinkAccountLoading, setIsLinkAccountLoading] = useState(false);
  const [isVerificationModal, setIsVerificationModal] = useState(false);
  const [radioSelect, setRadioSelect] = useState(0);
  const [isOtpModalVisible, setIsOtpModalVisible] = useState(false);
  const [statusType, setStatusType] = useState('');
  const [otpUniqueID, setOTPUniqueId] = useState('');
  const [linkedAccounts, setLinkedAccounts] = useState(
    new Array<LinkedDelinkedAccountObject>()
  );
  const [approvalStatus, setApprovalStatus] = useState('');
  const history = useHistory();
  const location = useLocation();

  const locationState: any = location.state;
  const { t } = useTranslation();

  useEffect(() => {
    if (type === ConsentApprovalStatusType.PENDING) {
      getLinkedAccountsOfUser()
        .then((res) => res.data)
        .then((res: LinkedDelinkedAccountResponse) => setLinkedAccounts(res));
    }
  }, [type]);

  const cardClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (selectedCard.indexOf(event.currentTarget.id) === -1) {
      setSelectedCard([...selectedCard, event.currentTarget.id]);
    } else {
      setSelectedCard(
        selectedCard.filter((card) => card !== event.currentTarget.id)
      );
    }
  };

  const approvalConsent = (consentApprovalType: string) => {
    setIsLinkAccountLoading(true);
    setIsVerificationModal(true);
    setApprovalStatus(consentApprovalType);
  };

  const radioSelectEvent = (e: RadioChangeEvent) =>
    setRadioSelect(e.target.value);

  const closeModal = () => {
    setIsVerificationModal(false);
    setIsLinkAccountLoading(false);
  };

  const redirect = (status: string, errorcode: string) => {
    const push = history.push;
    if (
      !!localStorage.getItem('redirect') &&
      !!localStorage.getItem('txnid') &&
      !!localStorage.getItem('sessionid')
    ) {
      const encodeParam = {
        status,
        errorcode,
        txnid: localStorage.getItem('txnid') || '',
        sessionid: localStorage.getItem('sessionid') || '',
        srcref: localStorage.getItem('srcref') || '',
        userid: localStorage.getItem('userid') || '',
      };

      encode(encodeParam).then((res) => {
        window.location.replace(
          `${localStorage.getItem('redirect')}?fi=${res.data.fi}&resdate=${
            res.data.resdate
          }&ecres=${res.data.ecres}`
        );
        localStorage.clear();
      });
    } else {
      push('/consent');
    }
  };

  const mapToLinkedRefNumberAndFIPHandle = () => {
    const accounts: SelectedAccounts[] = linkedAccounts
      .filter((account, index) => selectedCard.indexOf(index.toString()) !== -1)
      .map((account) => {
        return {
          linkRefNumber: account.linkRefNumber,
          fipHandle: account.fipHandle,
        };
      });
    return accounts;
  };

  const onSelectionModal = (pinValue: string) => {
    const push = history.push;
    if (type === ConsentApprovalStatusType.PENDING) {
      if (radioSelect === 0) {
        consentApprove({
          consentHandle: consentUniqueId,
          constentApprovalStatus: approvalStatus,
          authType: authType.PIN,
          authValue: pinValue,
          accounts: mapToLinkedRefNumberAndFIPHandle(),
          otpUniqueID,
        })
          .then(() => {
            approvalStatus === 'FAILED'
              ? notification.error({ message: t('Consent Rejected') })
              : notification.success({
                  message: t('Consent Approved'),
                });

            setIsVerificationModal(false);

            const conHandle = localStorage.getItem('srcref');
            if (!!conHandle && conHandle === consentUniqueId) {
              if (approvalStatus === 'FAILED') {
                redirect('F', '1');
              } else {
                redirect('S', '0');
              }
            } else {
              push('/consent');
            }
          })
          .finally(() => setIsLinkAccountLoading(false));
      } else {
        consentApprovalInitiation(consentUniqueId, approvalStatus)
          .then((res) => res.data)
          .then((res) => {
            setIsOtpModalVisible(true);
            setIsVerificationModal(false);
            setOTPUniqueId(res.otpUniqueID);
          })
          .finally(() => setIsLinkAccountLoading(false));
      }
    } else {
      if (radioSelect === 0) {
        updateConsentStatusVerification({
          consentId: consentUniqueId,
          consentStatus: statusType,
          authType: authType.PIN,
          authValue: pinValue,
        })
          .then(() => {
            notification.success({
              message: t('Consent Status Updated'),
            });
            setIsVerificationModal(false);
            history.push(Routes.consent);
          })
          .finally(() => setIsLinkAccountLoading(false));
      } else {
        updateConsentStatus(consentUniqueId, statusType)
          .then((res) => res.data)
          .then((res) => {
            setIsOtpModalVisible(true);
            setIsVerificationModal(false);
            setOTPUniqueId(res.otpUniqueID);
          })
          .finally(() => setIsLinkAccountLoading(false));
      }
    }
  };

  const updateConsentByStatus = (updateStatusType: string) => {
    setIsVerificationModal(true);
    setStatusType(updateStatusType);
  };

  const verifyOTPAuth = (otp: string) => {
    const push = history.push;
    if (type === ConsentApprovalStatusType.PENDING) {
      consentApprove({
        consentHandle: consentUniqueId,
        constentApprovalStatus: approvalStatus,
        authType: authType.OTP,
        authValue: otp,
        accounts: mapToLinkedRefNumberAndFIPHandle(),
        otpUniqueID,
      })
        .then(() => {
          approvalStatus === 'FAILED'
            ? notification.error({ message: t('Consent Rejected') })
            : notification.success({
                message: t('Consent Approved'),
              });
          setIsOtpModalVisible(false);

          const conHandle = localStorage.getItem('srcref');
          if (!!conHandle && conHandle === consentUniqueId) {
            if (approvalStatus === 'FAILED') {
              redirect('F', '1');
            } else {
              redirect('S', '0');
            }
          } else {
            push('/consent');
          }
        })
        .finally(() => setIsLinkAccountLoading(false));
    } else {
      updateConsentStatusVerification({
        consentId: consentUniqueId,
        consentStatus: statusType,
        authType: authType.OTP,
        authValue: otp,
        otpUniqueID,
      })
        .then(() => {
          notification.success({
            message: t('Consent Status Updated'),
          });
          setIsOtpModalVisible(false);
          history.push(Routes.consent);
        })
        .finally(() => setIsLinkAccountLoading(false));
    }
  };

  const onClose = () => {
    setIsOtpModalVisible(false);
    setIsLinkAccountLoading(false);
  };

  useEffect(() => {
    if (getIsRedirection()) {
      setRadioSelect(1);
    }
  }, []);
  return (
    <>
      <ConsentStatusApproval
        typeOfModal={approvalStatus ? approvalStatus : statusType}
        isAuthTypeSelectionModalVisible={isVerificationModal}
        onCloseAuthTypeSelectionModal={closeModal}
        onAuthTypeModalSubmit={onSelectionModal}
        onAuthTypeRadioSelectionChange={radioSelectEvent}
        selectedAuthType={radioSelect}
        verifyOTPAuthType={verifyOTPAuth}
        isAuthTypeOTPModalVisible={isOtpModalVisible}
        onCloseOTPModal={onClose}
      />

      <Card loading={false} className='consentDetails'>
        {type === ConsentStatusType.PAUSED ? (
          <PausedConsent
            consentUniqueId={consentUniqueId}
            type={type}
            onConsentStatusUpdate={updateConsentByStatus}
            createdOrUpdatedOn={
              locationState && locationState.createdOrUpdatedOn
            }
          />
        ) : (
          <></>
        )}
        {type === ConsentStatusType.ACTIVE ? (
          <ActiveConsent
            consentUniqueId={consentUniqueId}
            type={type}
            onConsentStatusUpdate={updateConsentByStatus}
            createdOrUpdatedOn={
              locationState && locationState.createdOrUpdatedOn
            }
          />
        ) : (
          <></>
        )}
        {type === ConsentApprovalStatusType.PENDING ? (
          <PendingConsent
            selectedCard={selectedCard}
            isLinkAccountLoading={isLinkAccountLoading}
            linkedAccounts={linkedAccounts}
            consentUniqueId={consentUniqueId}
            type={type}
            cardClick={cardClick}
            approvalConsent={approvalConsent}
            createdOrUpdatedOn={
              locationState && locationState.createdOrUpdatedOn
            }
          />
        ) : (
          <></>
        )}
        {type === ConsentStatusType.REVOKED ? (
          <RevokedConsent
            consentUniqueId={consentUniqueId}
            type={type}
            createdOrUpdatedOn={
              locationState && locationState.createdOrUpdatedOn
            }
          />
        ) : (
          <></>
        )}
        {type === ConsentStatusType.REJECTED ||
        type === ConsentStatusType.EXPIRED ? (
          <RejectedExpiredConsent
            type={type}
            consentUniqueId={consentUniqueId}
            createdOrUpdatedOn={
              locationState && locationState.createdOrUpdatedOn
            }
          />
        ) : (
          <></>
        )}
      </Card>
    </>
  );
};

export default ConsentDetails;
